import React, { useMemo } from "react";

import { Bar, BarChart, CartesianGrid, ComposedChart, Line, Tooltip as RechartsTooltip, ResponsiveContainer, XAxis, YAxis } from "recharts";

import { formatNumber, useSoilSightCurrency, useTranslation } from "utils";

import {
  BAR_GROUP_INNER_GAP_PX,
  BASELINE_BAR_STRIPE_COLORS,
  CHART_ACTIVE_DOT_RADIUS,
  CHART_AXIS_COLOR,
  CHART_GRID_STROKE,
  CHART_LINE_WIDTH,
  CHART_MARGIN,
  FORECAST_CHART_HEIGHT,
  FORECAST_Y_AXIS_WIDTH_PX,
  PAGE_AXIS_STYLE,
  REPORT_AXIS_STYLE,
  ROOT_FONT_SIZE_PX,
} from "consts";

import { Typography } from "components";

import { HorizontalScrollFade } from "pages/app/scenario-analysis/components/HorizontalScrollFade";
import { OutcomeTooltip } from "pages/app/scenario-analysis/components/OutcomeTooltip";
import { useMeasuredWidth } from "pages/app/scenario-analysis/hooks/useMeasuredWidth";
import { useSvgId } from "pages/app/scenario-analysis/hooks/useSvgId";
import { useYearLabel } from "pages/app/scenario-analysis/hooks/useYearLabel";
import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { leftAlignedYAxisTick, yearXAxisProps } from "pages/app/scenario-analysis/utils/chartAxes";
import {
  ForecastDatum,
  ViewMode,
  buildForecastDataFromSoilSight,
  forecastKey,
  getBandKinds,
  getBarWidthByScenarioCount,
  getStretchedBarGroupWidthPx,
  scenarioLineColorAt,
  shouldUseOutcomeYearScroll,
} from "pages/app/scenario-analysis/utils/forecast";
import { tourTargetProps } from "pages/app/scenario-analysis/utils/outcomesTourTarget";

type ForecastChartProps = {
  scenarios: ScenarioData[];
  projectionYears: number;
  viewMode?: ViewMode;
  /** Soil Sight–derived timeline; omit to chart empty placeholders until outcomes exist. */
  forecastData?: ForecastDatum[];
  /** Crop name lookup keyed by cropId for tooltip labels. */
  mainCropLabelLookup?: Record<string, string>;
  dataTestId?: string;
  /** "page" uses smaller, darker axis labels; the PDF report keeps the larger grey ones. */
  variant?: "page" | "report";
};

type TooltipPayloadItem = {
  dataKey?: string;
  value?: number;
  color?: string;
  fill?: string;
  name?: string;
  payload?: Record<string, number | string>;
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  mode: ViewMode;
  formatCmOutcome: (value: number, opts?: { decimals?: number }) => string;
  mainCropLabelLookup: Record<string, string>;
  formatYearLabel: (yearNum: number) => string;
};

const BaselineBarPattern = ({ patternId }: { patternId: string }) => (
  <defs>
    <pattern id={patternId} patternUnits="userSpaceOnUse" width="6" height="8" patternTransform="rotate(-120)">
      <rect width="2" height="8" fill={BASELINE_BAR_STRIPE_COLORS.stripe} />
      <rect x="4" width="4" height="8" fill={BASELINE_BAR_STRIPE_COLORS.gap} />
    </pattern>
  </defs>
);

export const CustomTooltip = ({ active, payload, mode, formatCmOutcome, mainCropLabelLookup, formatYearLabel }: CustomTooltipProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  if (!active || !payload?.length) return null;

  const suffix = mode === "cumulative" ? "_cumulative" : mode === "annual" ? "_annual" : "_yield";
  const activePayload = payload.filter((entry) => entry.dataKey?.endsWith(suffix));
  if (!activePayload.length) return null;

  const yearNum = Number(activePayload[0]?.payload?.yearNum ?? 0);
  const bandKinds = getBandKinds(mode);
  const formatValue = (value: number) =>
    mode === "yield" ? `${formatNumber({ number: value, decimalPlaces: 1 })} ${t("outcomeComparison.unit.yield")}` : formatCmOutcome(value);

  const rows = activePayload.map((entry, idx) => {
    const match = entry.dataKey?.match(/scenario_(\d+)/);
    const scenarioIdx = match ? Number(match[1]) : null;
    const midVal = typeof entry.value === "number" ? entry.value : 0;
    const row = scenarioIdx !== null ? entry.payload : undefined;

    const cropId = row ? String(row[forecastKey(scenarioIdx ?? 0, "cropId")] ?? "") : "";
    const low = row && bandKinds ? Number(row[forecastKey(scenarioIdx ?? 0, bandKinds.low)] ?? midVal) : midVal;
    const high = row && bandKinds ? Number(row[forecastKey(scenarioIdx ?? 0, bandKinds.high)] ?? midVal) : midVal;
    const showRange = Boolean(row && bandKinds) && formatValue(low) !== formatValue(high);

    return {
      key: `${entry.dataKey}-${idx}`,
      color: scenarioIdx !== null ? scenarioLineColorAt(scenarioIdx) : entry.color ?? entry.fill ?? CHART_AXIS_COLOR,
      name: cropId && mainCropLabelLookup[cropId] ? mainCropLabelLookup[cropId] : entry.name ?? "",
      value: formatValue(midVal),
      range: showRange ? `${formatValue(low)} – ${formatValue(high)}` : undefined,
      rangeTestId: "forecast-tooltip-range",
    };
  });

  return <OutcomeTooltip header={formatYearLabel(yearNum)} rows={rows} />;
};

export const ForecastChart = ({
  scenarios,
  projectionYears,
  viewMode = "cumulative",
  forecastData: forecastDataProp,
  mainCropLabelLookup = {},
  dataTestId = "forecast-chart",
  variant = "page",
}: ForecastChartProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { formatYear } = useYearLabel();
  const { formatCmOutcome, currencySymbol } = useSoilSightCurrency();

  const baselinePatternId = useSvgId("baseline-bar-stripes");
  const isBarView = viewMode === "annual" || viewMode === "yield";
  const isYearScrollEnabled = isBarView && shouldUseOutcomeYearScroll(projectionYears);

  // Canvas width drives how far the scrollable bar chart stretches its year groups; only the scroll layout reads it.
  const [canvasRef, canvasWidth] = useMeasuredWidth<HTMLDivElement>({ enabled: isYearScrollEnabled });

  const data = useMemo<ForecastDatum[]>(
    () => forecastDataProp ?? buildForecastDataFromSoilSight(scenarios, projectionYears, {}),
    [forecastDataProp, scenarios, projectionYears]
  );

  const isYield = viewMode === "yield";
  const unitLabel = isYield
    ? t("outcomeComparison.legendUnitYield", { years: String(projectionYears) })
    : t("outcomeComparison.legendUnit", { years: String(projectionYears), currency: currencySymbol });
  const axisStyle = variant === "page" ? PAGE_AXIS_STYLE : REPORT_AXIS_STYLE;

  const yAxisFormatter = (v: number) => {
    if (isYield) return v.toFixed(1);
    return formatNumber({ number: Math.round(v), decimalPlaces: 0 });
  };

  const scenarioCount = scenarios.length;
  const barSize = getBarWidthByScenarioCount(scenarioCount);
  const barKind = viewMode === "yield" ? "yield" : "annual";
  // In scroll mode the y-axis lives in its own sticky chart, flush with the canvas left edge; it and the
  // scrolled chart's right margin sit outside the year bands, so they are excluded from the stretch math.
  const axisOverheadPx = FORECAST_Y_AXIS_WIDTH_PX + CHART_MARGIN.right;
  const barGroupWidth = getStretchedBarGroupWidthPx(scenarioCount, canvasWidth - axisOverheadPx);
  const chartWidth = isYearScrollEnabled ? projectionYears * barGroupWidth + axisOverheadPx : undefined;
  const scrollChartWidth = chartWidth !== undefined ? chartWidth - FORECAST_Y_AXIS_WIDTH_PX : undefined;

  const tooltipContent = (
    <CustomTooltip mode={viewMode} formatCmOutcome={formatCmOutcome} mainCropLabelLookup={mainCropLabelLookup} formatYearLabel={formatYear} />
  );

  const yearTickFormatter = (value: string | number) => t("outcomeComparison.chartYearTick", { year: String(value) });
  const xAxis = <XAxis dataKey="year" {...yearXAxisProps(axisStyle)} tickFormatter={yearTickFormatter} />;
  const yAxisNode = (hide = false) => (
    <YAxis hide={hide} tick={leftAlignedYAxisTick(axisStyle, yAxisFormatter)} tickLine={false} axisLine={false} width={FORECAST_Y_AXIS_WIDTH_PX} />
  );

  const barChartNode = ({ width, hideYAxis = false }: { width?: number; hideYAxis?: boolean } = {}) => (
    <BarChart
      width={width}
      height={FORECAST_CHART_HEIGHT}
      data={data}
      margin={hideYAxis ? { ...CHART_MARGIN, left: 0 } : CHART_MARGIN}
      barGap={BAR_GROUP_INNER_GAP_PX}
      barCategoryGap="12%"
    >
      <BaselineBarPattern patternId={baselinePatternId} />
      <CartesianGrid vertical={false} stroke={CHART_GRID_STROKE} />
      {xAxis}
      {yAxisNode(hideYAxis)}
      <RechartsTooltip content={tooltipContent} />
      {scenarios.map((scenario, idx) => {
        const lineColor = scenarioLineColorAt(idx);
        return (
          <Bar
            key={`bar-${scenario.id}-${viewMode}`}
            dataKey={forecastKey(idx, barKind)}
            name={scenario.title}
            fill={scenario.isBaseline ? `url(#${baselinePatternId})` : lineColor}
            fillOpacity={scenario.isBaseline ? 1 : 0.9}
            radius={[3, 3, 0, 0]}
            barSize={barSize}
            isAnimationActive={false}
          />
        );
      })}
    </BarChart>
  );

  // Y-axis-only chart pinned to the left edge in scroll mode. It mirrors the scrolled chart's
  // data through invisible bars so both charts compute the same y domain and ticks.
  const stickyYAxisChart = (
    <BarChart width={FORECAST_Y_AXIS_WIDTH_PX} height={FORECAST_CHART_HEIGHT} data={data} margin={{ ...CHART_MARGIN, left: 0, right: 0 }}>
      <XAxis dataKey="year" tick={false} tickLine={false} axisLine={false} interval={0} />
      {yAxisNode()}
      {scenarios.map((scenario, idx) => (
        <Bar key={`axis-bar-${scenario.id}-${viewMode}`} dataKey={forecastKey(idx, barKind)} fill="transparent" isAnimationActive={false} />
      ))}
    </BarChart>
  );

  const lineChart = (
    <ComposedChart data={data} margin={CHART_MARGIN}>
      <CartesianGrid vertical={false} stroke={CHART_GRID_STROKE} />
      {xAxis}
      {yAxisNode()}
      <RechartsTooltip content={tooltipContent} />
      {scenarios.map((scenario, idx) => {
        const lineColor = scenarioLineColorAt(idx);
        return (
          <Line
            key={`line-${scenario.id}-${viewMode}`}
            type="natural"
            dataKey={forecastKey(idx, "cumulative")}
            name={scenario.title}
            stroke={lineColor}
            strokeWidth={CHART_LINE_WIDTH}
            strokeDasharray={scenario.isBaseline ? "6 4" : undefined}
            dot={false}
            activeDot={{ r: CHART_ACTIVE_DOT_RADIUS, fill: lineColor }}
            isAnimationActive={false}
          />
        );
      })}
    </ComposedChart>
  );

  const renderChart = () => {
    if (!isBarView) {
      return (
        <ResponsiveContainer width="100%" height={FORECAST_CHART_HEIGHT}>
          {lineChart}
        </ResponsiveContainer>
      );
    }

    if (isYearScrollEnabled) {
      return (
        <HorizontalScrollFade dataTestId={`${dataTestId}-scroll`} chromeOffsetLeft={FORECAST_Y_AXIS_WIDTH_PX / ROOT_FONT_SIZE_PX} enabled>
          <div
            className="outcome-comparison-chart-canvas-inner outcome-comparison-chart-canvas-inner--scroll"
            style={{ width: chartWidth, minWidth: chartWidth }}
          >
            <div className="outcome-comparison-chart-sticky-y-axis" data-testid={`${dataTestId}-sticky-y-axis`}>
              {stickyYAxisChart}
            </div>
            {barChartNode({ width: scrollChartWidth, hideYAxis: true })}
          </div>
        </HorizontalScrollFade>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={FORECAST_CHART_HEIGHT}>
        {barChartNode()}
      </ResponsiveContainer>
    );
  };

  return (
    <div className="outcome-comparison-chart" data-testid={dataTestId} {...tourTargetProps("chart")}>
      <div className="outcome-comparison-chart-legend-row">
        <Typography variant="paragraph-s" color="grey-600" className="outcome-comparison-chart-unit">
          {unitLabel}
        </Typography>
        <div className="outcome-comparison-chart-legend" data-testid={`${dataTestId}-legend`}>
          {scenarios.map((scenario, idx) => {
            const lineColor = scenarioLineColorAt(idx);
            return (
              <div key={scenario.id} className="outcome-comparison-chart-legend-item">
                <span className="outcome-comparison-chart-swatch outcome-comparison-chart-swatch--dot" style={{ backgroundColor: lineColor }} aria-hidden />
                <Typography variant="paragraph-s" color="grey-600">
                  {scenario.title}
                </Typography>
              </div>
            );
          })}
        </div>
      </div>
      <div ref={canvasRef} className="outcome-comparison-chart-canvas">
        {renderChart()}
      </div>
    </div>
  );
};

export default ForecastChart;
